// Shared Help Q&A contract (typed help/intake framework, commit 5).
//
// The general-question vertical has one thing that MUST NOT be restated in two
// places: the closed set of reasons a question was not deflected. The server
// decides deflection (never the client) and returns reason CODES; the Help Q&A
// page turns those codes into plain language. If the two lists drifted, a
// reporter would be shown "PRIME could not answer" with no explanation, or worse,
// an explanation that does not match what happened.
//
// So the codes and their reporter-facing labels live here, together, and both
// runtimes import them. Pure data plus pure functions: no React, no API, no
// Node-only imports.
//
// Nothing in this module decides anything. The decision is
// apps/api/src/services/questionDeflection.service.js, server side, and it is the
// only thing allowed to produce these codes.

// A question is one message turn, so it shares the intake message budget rather
// than inventing a second "how much can I type" rule.
export const HELP_QUESTION_MAX_LENGTH = 4000;
// The generated title of a question intake is derived from the question itself,
// clipped to something a reviewer can scan in a queue row.
export const HELP_QUESTION_TITLE_MAX_LENGTH = 160;

// Why a question was NOT deflected. One code per independent clause of the
// deflection policy, so a reporter (and an auditor) can always tell WHICH
// requirement failed rather than being told "low confidence".
//
// Ordered from "PRIME never really ran" to "PRIME ran but the answer does not
// hold up", which is also the order the policy evaluates them in.
export const QUESTION_DEFLECTION_REASONS = Object.freeze([
  // Provenance clauses — the answer did not come from the live PRIME knowledge
  // base at all.
  'provider_not_live_kb',
  'provider_fallback_occurred',
  'model_not_invoked',
  'seeded_or_illustrative_grounding',
  'partial_execution',
  // The provenance object did not EXPLICITLY assert the facts the policy needs.
  // Added by the commit-5 adversarial audit: a partially populated provenance used
  // to pass, because a missing `seeded` or `fallback` field read as "safe". A policy
  // that treats an absent fact as a favourable one is fail-open.
  'provenance_incomplete',
  // Retrieval clauses.
  'retrieval_not_live_kb',
  'retrieval_failed',
  'retrieval_empty',
  'insufficient_evidence',
  'top_score_below_threshold',
  // Grounding clauses.
  'answer_empty',
  // The model was ASKED, in the structured contract, whether the passages answer the
  // question, and it did not answer yes. This is the PRIMARY answerability signal:
  // the commit-5 audit reproduced a deflection of the answer "The passages do not
  // contain an answer [1] [2]", which satisfied every threshold and every citation
  // rule because answerability was being inferred from prose against a finite phrase
  // list. A required boolean cannot be missed by a phrase the list does not contain.
  'answer_not_answerable',
  'insufficient_citations',
  // The bound citations were numerous enough but resolve to too few DISTINCT
  // documents — two chunks of one file are one source, and the refusal must say
  // that rather than falsely claiming there were not enough citations.
  'distinct_source_count_below_minimum',
  'citations_not_grounded',
  'answer_missing_citation_marker',
  // Defense in depth behind `answer_not_answerable`, not the mechanism.
  'answer_states_insufficiency',
]);

export function isQuestionDeflectionReason(value) {
  return QUESTION_DEFLECTION_REASONS.includes(value);
}

// Reporter-facing wording. Plain sentences, no scores, no provider names a user
// would have to look up — the assurance indicator carries the source count, and
// these carry the reason PRIME is handing the question to a person.
export const QUESTION_DEFLECTION_REASON_LABELS = Object.freeze({
  provider_not_live_kb:
    'PRIME’s live knowledge base did not answer this question, so the answer above is not verified PRIME guidance.',
  provider_fallback_occurred:
    'PRIME’s knowledge base was unavailable and a stand-in answered instead.',
  model_not_invoked: 'No PRIME answering service ran for this question.',
  seeded_or_illustrative_grounding:
    'The answer above came from PRIME’s demonstration content, not the live knowledge base.',
  partial_execution: 'PRIME only partly completed the search, so the answer is incomplete.',
  provenance_incomplete:
    'PRIME could not confirm how this answer was produced, so it is not treating it as verified guidance.',
  retrieval_not_live_kb: 'PRIME did not search the live knowledge base for this question.',
  retrieval_failed: 'PRIME could not search its knowledge base.',
  retrieval_empty: 'PRIME found no source material for this question.',
  insufficient_evidence: 'PRIME found too little source material to rely on.',
  top_score_below_threshold: 'Nothing PRIME found was a close enough match to your question.',
  answer_empty: 'PRIME did not produce an answer.',
  answer_not_answerable: 'PRIME reported that its own sources do not answer this question.',
  insufficient_citations: 'The answer is not backed by enough PRIME sources.',
  distinct_source_count_below_minimum:
    'The answer’s support comes from too few distinct PRIME documents.',
  citations_not_grounded: 'The answer cited material PRIME did not actually retrieve.',
  answer_missing_citation_marker: 'The answer does not point at the sources it came from.',
  answer_states_insufficiency: 'PRIME said its own sources were not enough to answer this.',
});

export function describeQuestionDeflectionReason(code) {
  return (
    QUESTION_DEFLECTION_REASON_LABELS[code] ||
    'PRIME could not confirm this answer is reliable enough to stand on its own.'
  );
}

// How the deflection outcome of a question intake is worded for the reporter.
// `deflected` is the state after a grounded answer; the two submitted states
// distinguish "PRIME could not answer, so a person will" from "PRIME answered
// and I still want a person".
export const QUESTION_OUTCOME_LABELS = Object.freeze({
  deflected: 'Answered by PRIME',
  handoff_submitted: 'Sent to the team because PRIME could not answer it',
  asked_team_anyway: 'Answered by PRIME, and sent to the team as well',
});

/**
 * The plain-language assurance sentence for a DEFLECTED answer.
 *
 * Deliberately a source COUNT, never a raw score: "0.71" is not information a
 * reporter can act on, and presenting it as confidence implies a precision the
 * retrieval score does not carry.
 */
export function describeAnswerAssurance(citationCount) {
  const count = Number.isFinite(citationCount) ? Math.max(0, Math.trunc(citationCount)) : 0;
  if (count === 0) return 'This answer is not grounded in any PRIME source.';
  return `This answer is grounded in ${count} PRIME source${count === 1 ? '' : 's'}.`;
}

// The generated intake title for a question. Derived from the question so the
// reviewer queue row reads as the thing that was asked, and clipped rather than
// truncated mid-word where a word boundary is close enough to the limit.
export function titleFromQuestion(question) {
  const text = String(question ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return null;
  if (text.length <= HELP_QUESTION_TITLE_MAX_LENGTH) return text;
  const clipped = text.slice(0, HELP_QUESTION_TITLE_MAX_LENGTH - 1);
  const lastSpace = clipped.lastIndexOf(' ');
  const base =
    lastSpace > HELP_QUESTION_TITLE_MAX_LENGTH - 40 ? clipped.slice(0, lastSpace) : clipped;
  return `${base}…`;
}
